import ArticleIcon from '@mui/icons-material/Article';
import CodeIcon from '@mui/icons-material/Code';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import LinkIcon from '@mui/icons-material/Link';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import StatusSwitch from 'src/components/switch/status-switch.component';
import {
  defaultArticle,
  DomainSummary,
  getHelpBaseUrl,
  htmlToPreviewText,
  KBSetupShell,
  sanitizeKBHtml,
  toPathSlug,
} from './kbSetupShared';

export default function KBArticlesSetup() {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const [loader, setLoader] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [kbSettings, setKBSettings] = useState({ is_enabled: true });
  const [saveLoader, setSaveLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [collections, setCollections] = useState([]);
  const [articles, setArticles] = useState([]);
  const [search, setSearch] = useState('');
  const [articleForm, setArticleForm] = useState(defaultArticle);
  const [articleEditorMode, setArticleEditorMode] = useState('editor');
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const articleEditorRef = useRef(null);
  const canManageKB = CommonHelper.CheckForPermissionAvailableForUser(CommonHelper.PermissionList.KB_MANAGE);
  const helpBaseUrl = useMemo(() => getHelpBaseUrl(), []);
  const setupDisabled = !kbSettings.is_enabled;
  const disabledContentSx = setupDisabled
    ? {
        opacity: 0.45,
        filter: 'grayscale(0.35)',
        transition: 'opacity 180ms ease, filter 180ms ease',
      }
    : {
        opacity: 1,
        transition: 'opacity 180ms ease, filter 180ms ease',
      };
  const toggleInFlightRef = useRef(false);

  const fetchKBData = useCallback(
    (searchText = '') => {
      if (!canManageKB) {
        setLoader(false);
        return;
      }
      setLoader(true);
      HttpServices.Get({
        url: userModuleURL.kbCollections,
        successCallback: (collectionData) => {
          HttpServices.Get({
            url: `${userModuleURL.kbArticles}${searchText ? `?q=${encodeURIComponent(searchText)}` : ''}`,
            successCallback: (articleData) => {
              setCollections(Array.isArray(collectionData) ? collectionData : []);
              setArticles(Array.isArray(articleData) ? articleData : []);
              setLoader(false);
            },
            failureCallback: (message) => {
              setLoader(false);
              message && showToast(message, 'e');
            },
          });
        },
        failureCallback: (message) => {
          setLoader(false);
          message && showToast(message, 'e');
        },
      });
    },
    [canManageKB, showToast]
  );

  useEffect(() => {
    fetchKBData();
  }, [fetchKBData]);

  const fetchKBSettings = useCallback(() => {
    if (!canManageKB) return;

    HttpServices.Get({
      url: userModuleURL.kbSettings,
      successCallback: (data) => {
        setKBSettings({ is_enabled: data?.is_enabled !== false });
      },
      failureCallback: (message) => {
        message && showToastRef.current(message, 'e');
      },
    });
  }, [canManageKB]);

  useEffect(() => {
    fetchKBSettings();
  }, [fetchKBSettings]);

  const toggleKBSettings = useCallback(
    (event) => {
      if (toggleInFlightRef.current) return;

      const nextEnabled = event.target.checked;
      let previousEnabled = true;

      setKBSettings((prev) => {
        previousEnabled = Boolean(prev.is_enabled);
        return prev.is_enabled === nextEnabled ? prev : { ...prev, is_enabled: nextEnabled };
      });

      toggleInFlightRef.current = true;
      setIsSavingSettings(true);

      HttpServices.Post({
        url: userModuleURL.kbSettings,
        body: { is_enabled: nextEnabled },
        successCallback: (data, message) => {
          const enabled = data?.is_enabled !== false;
          setKBSettings((prev) =>
            prev.is_enabled === enabled ? prev : { ...prev, is_enabled: enabled }
          );
          toggleInFlightRef.current = false;
          setIsSavingSettings(false);
          message && showToastRef.current(message, 's');
        },
        failureCallback: (message) => {
          setKBSettings((prev) =>
            prev.is_enabled === previousEnabled ? prev : { ...prev, is_enabled: previousEnabled }
          );
          toggleInFlightRef.current = false;
          setIsSavingSettings(false);
          message && showToastRef.current(message, 'e');
        },
      });
    },
    []
  );

  const headerAction = useMemo(() => {
    if (!canManageKB) return null;

    return (
      <StatusSwitch
        checked={Boolean(kbSettings.is_enabled)}
        onChange={toggleKBSettings}
        inputProps={{
          'aria-label': kbSettings.is_enabled
            ? translate('kb_setup_page.disable_kb', { defaultValue: 'Disable Knowledge Base' })
            : translate('kb_setup_page.enable_kb', { defaultValue: 'Enable Knowledge Base' }),
        }}
        switchSx={{
          opacity: isSavingSettings ? 0.7 : 1,
          transition: 'opacity 0.15s ease',
        }}
      />
    );
  }, [canManageKB, isSavingSettings, kbSettings.is_enabled, toggleKBSettings, translate]);

  const copyHelpDomain = () => {
    if (!navigator?.clipboard) return;
    navigator.clipboard.writeText(helpBaseUrl);
    showToast(translate('kb_setup_page.domain_copied', { defaultValue: 'Knowledge base URL copied' }), 's');
  };

  const saveArticle = () => {
    if (setupDisabled) return;
    if (!articleForm.title.trim() || !articleForm.content.trim()) {
      showToast(translate('kb_setup_page.article_required', { defaultValue: 'Article title and content are required' }), 'e');
      return;
    }
    setSaveLoader(true);
    HttpServices.Post({
      url: userModuleURL.kbArticles,
      body: {
        ...articleForm,
        content: sanitizeKBHtml(articleForm.content),
        collection_id: articleForm.collection_id ? Number(articleForm.collection_id) : null,
      },
      successCallback: (_data, message) => {
        setArticleForm(defaultArticle);
        setArticleDialogOpen(false);
        setSaveLoader(false);
        fetchKBData(search);
        message && showToast(message, 's');
      },
      failureCallback: (message) => {
        setSaveLoader(false);
        message && showToast(message, 'e');
      },
    });
  };

  const openAddArticleDialog = () => {
    if (setupDisabled) return;
    setArticleForm(defaultArticle);
    setArticleEditorMode('editor');
    setArticleDialogOpen(true);
  };

  const closeArticleDialog = () => {
    if (saveLoader) return;
    setArticleDialogOpen(false);
    setArticleForm(defaultArticle);
  };

  const editArticle = (article) => {
    if (setupDisabled) return;
    const nextArticle = {
      id: article.id,
      collection_id: article.collection_id || '',
      title: article.title || '',
      slug: article.slug || '',
      content: article.content || '',
      status: article.status || 'draft',
    };
    setArticleForm(nextArticle);
    setArticleEditorMode('editor');
    setArticleDialogOpen(true);
    window.setTimeout(() => {
      if (articleEditorRef.current) {
        articleEditorRef.current.innerHTML = sanitizeKBHtml(nextArticle.content || '');
      }
    }, 0);
  };

  const deleteArticle = () => {
    if (!deleteTarget || setupDisabled) return;
    setDeleteLoader(true);
    HttpServices.Delete({
      url: `${userModuleURL.kbArticles}/${deleteTarget.id}`,
      successCallback: (_data, message) => {
        if (articleForm.id === deleteTarget.id) {
          setArticleForm(defaultArticle);
        }
        fetchKBData(search);
        setDeleteLoader(false);
        setDeleteTarget(null);
        message && showToast(message, 's');
      },
      failureCallback: (message) => {
        setDeleteLoader(false);
        message && showToast(message, 'e');
      },
    });
  };

  const publishArticle = (article) => {
    if (setupDisabled) return;
    HttpServices.Post({
      url: `${userModuleURL.kbArticles}/${article.id}/publish`,
      body: {},
      successCallback: (_data, message) => {
        fetchKBData(search);
        message && showToast(message, 's');
      },
      failureCallback: (message) => message && showToast(message, 'e'),
    });
  };

  const hydrateArticleEditor = useCallback(() => {
    if (articleEditorMode !== 'editor' || !articleEditorRef.current) return;
    const nextHtml = sanitizeKBHtml(articleForm.content || '');
    if (articleEditorRef.current.innerHTML !== nextHtml) {
      articleEditorRef.current.innerHTML = nextHtml;
    }
  }, [articleEditorMode, articleForm.content]);

  useEffect(() => {
    if (!articleDialogOpen) return;
    hydrateArticleEditor();
  }, [articleDialogOpen, hydrateArticleEditor]);

  const syncArticleFromEditor = () => {
    const editor = articleEditorRef.current;
    setArticleForm((prev) => ({ ...prev, content: sanitizeKBHtml(editor?.innerHTML || '').trim() }));
  };

  const runArticleCommand = (command, value = null) => {
    if (setupDisabled) return;
    articleEditorRef.current?.focus();
    document.execCommand(command, false, value);
    syncArticleFromEditor();
  };

  const renderArticleEditor = () => (
    <Grid container spacing={2.5} sx={{ minWidth: 0 }}>
      <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
        <Stack spacing={1.5}>
          <TextField select fullWidth disabled={setupDisabled} label={translate('kb_setup_page.collection', { defaultValue: 'Collection' })} value={articleForm.collection_id} onChange={(e) => setArticleForm((prev) => ({ ...prev, collection_id: e.target.value }))}>
            <MenuItem value="">{translate('kb_setup_page.unassigned', { defaultValue: 'Unassigned' })}</MenuItem>
            {collections.map((collection) => <MenuItem key={collection.id} value={collection.id}>{collection.name}</MenuItem>)}
          </TextField>
          <TextField fullWidth disabled={setupDisabled} label={translate('kb_setup_page.article_title_label', { defaultValue: 'Title' })} value={articleForm.title} onChange={(e) => setArticleForm((prev) => ({ ...prev, title: e.target.value }))} />
          <TextField
            fullWidth
            disabled={setupDisabled}
            label={translate('kb_setup_page.slug', { defaultValue: 'Slug' })}
            value={articleForm.slug}
            helperText={translate('kb_setup_page.slug_help', {
              defaultValue: 'Lowercase letters, numbers, and hyphens only. Leave empty to auto-generate.',
            })}
            onChange={(e) => setArticleForm((prev) => ({ ...prev, slug: e.target.value }))}
          />
          <Stack spacing={1.25}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="caption" color="text.secondary">
                {articleEditorMode === 'html'
                  ? translate('kb_setup_page.html_editor_help', {
                      defaultValue: 'Paste or edit article HTML. Preview renders the saved content.',
                    })
                  : translate('kb_setup_page.answer_label', { defaultValue: 'Answer' })}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={articleEditorMode}
                disabled={setupDisabled}
                onChange={(_event, nextMode) => nextMode && setArticleEditorMode(nextMode)}
              >
                <ToggleButton value="editor" aria-label={translate('kb_setup_page.article_editor', { defaultValue: 'Article editor' })}>
                  <TextFieldsIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="html" aria-label={translate('kb_setup_page.article_html_content', { defaultValue: 'Article HTML content' })}>
                  <CodeIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {articleEditorMode === 'editor' ? (
              <>
                <Box
                  ref={articleEditorRef}
                  contentEditable={!setupDisabled}
                  suppressContentEditableWarning
                  role="textbox"
                  aria-label={translate('kb_setup_page.article_content', { defaultValue: 'Article content' })}
                  data-placeholder={translate('kb_setup_page.article_content_placeholder', {
                    defaultValue: 'Type the article content...',
                  })}
                  onInput={syncArticleFromEditor}
                  onBlur={syncArticleFromEditor}
                  sx={{
                    minHeight: 260,
                    maxHeight: '46vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    outline: 'none',
                    fontSize: 14,
                    bgcolor: 'background.paper',
                    opacity: setupDisabled ? 0.55 : 1,
                    pointerEvents: setupDisabled ? 'none' : 'auto',
                    '&:focus': {
                      borderColor: 'primary.main',
                      boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
                    },
                    '&:empty::before': {
                      content: 'attr(data-placeholder)',
                      color: 'text.disabled',
                      pointerEvents: 'none',
                    },
                    '& p': { my: 0.5 },
                    '& ul, & ol': { pl: 2.5, my: 0.5 },
                    '& *': {
                      maxWidth: '100%',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    },
                  }}
                />
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  <Tooltip title={translate('kb_setup_page.bold', { defaultValue: 'Bold' })}>
                    <IconButton size="small" disabled={setupDisabled} onClick={() => runArticleCommand('bold')}>
                      <FormatBoldIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={translate('kb_setup_page.italic', { defaultValue: 'Italic' })}>
                    <IconButton size="small" disabled={setupDisabled} onClick={() => runArticleCommand('italic')}>
                      <FormatItalicIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={translate('kb_setup_page.underline', { defaultValue: 'Underline' })}>
                    <IconButton size="small" disabled={setupDisabled} onClick={() => runArticleCommand('underline')}>
                      <FormatUnderlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={translate('kb_setup_page.bullet_list', { defaultValue: 'Bullet list' })}>
                    <IconButton size="small" disabled={setupDisabled} onClick={() => runArticleCommand('insertUnorderedList')}>
                      <FormatListBulletedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={translate('kb_setup_page.numbered_list', { defaultValue: 'Numbered list' })}>
                    <IconButton size="small" disabled={setupDisabled} onClick={() => runArticleCommand('insertOrderedList')}>
                      <FormatListNumberedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={translate('kb_setup_page.link', { defaultValue: 'Link' })}>
                    <IconButton size="small" disabled={setupDisabled} onClick={() => runArticleCommand('createLink', 'https://example.com')}>
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </>
            ) : (
              <TextField
                fullWidth
                disabled={setupDisabled}
                multiline
                minRows={12}
                label={translate('kb_setup_page.article_html_content', { defaultValue: 'Article HTML content' })}
                placeholder={translate('kb_setup_page.article_html_placeholder', {
                  defaultValue: '<h1>Article title</h1><p>Write or paste HTML content...</p>',
                })}
                value={articleForm.content}
                onChange={(e) => setArticleForm((prev) => ({ ...prev, content: sanitizeKBHtml(e.target.value) }))}
              />
            )}
          </Stack>
          <TextField select fullWidth disabled={setupDisabled} label={translate('kb_setup_page.status', { defaultValue: 'Status' })} value={articleForm.status} onChange={(e) => setArticleForm((prev) => ({ ...prev, status: e.target.value }))}>
            <MenuItem value="draft">{translate('kb_setup_page.draft', { defaultValue: 'Draft' })}</MenuItem>
            <MenuItem value="published">{translate('kb_setup_page.published', { defaultValue: 'Published' })}</MenuItem>
          </TextField>
        </Stack>
      </Grid>
      <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 1,
            bgcolor: '#fafafa',
            minHeight: '100%',
            maxHeight: { xs: 320, md: '58vh' },
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <Typography variant="caption" color="text.secondary">{translate('kb_setup_page.website_preview', { defaultValue: 'Website preview' })}</Typography>
          <Box
            sx={{
              mt: 2,
              color: '#111827',
              '& h1': { fontSize: 32, lineHeight: 1.2, mb: 1.5 },
              '& h2': { fontSize: 24, lineHeight: 1.3, mt: 2, mb: 1 },
              '& p': { lineHeight: 1.75, mb: 1.25 },
              '& ul, & ol': { pl: 3, mb: 1.25 },
              '& a': { color: 'primary.main', fontWeight: 600 },
              '& *': {
                maxWidth: '100%',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              },
            }}
            dangerouslySetInnerHTML={{
              __html: sanitizeKBHtml(
                articleForm.content ||
                  `<p>${translate('kb_setup_page.article_preview_placeholder', {
                    defaultValue: 'Your article preview appears here.',
                  })}</p>`
              ),
            }}
          />
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <KBSetupShell activeView="articles" title={translate('kb_setup_page.title', { defaultValue: 'Knowledge Base Setup' })} headerAction={headerAction}>
      {loader ? <LinearProgress /> : !canManageKB ? (
        <Typography variant="body2" color="text.secondary">
          {translate('kb_setup_page.permission_note', {
            defaultValue: 'Knowledge base setup is limited to users with KB manage access.',
          })}
        </Typography>
      ) : (
        <Stack spacing={2}>
          <DomainSummary helpBaseUrl={helpBaseUrl} disabled={setupDisabled} onCopy={copyHelpDomain} />
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, ...disabledContentSx }}>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                <Box>
                  <Typography variant="subtitle1">{translate('kb_setup_page.articles_title', { defaultValue: 'Articles' })}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {translate('kb_setup_page.articles_help', {
                      defaultValue: 'Create, preview, publish, and organize knowledge base articles.',
                    })}
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<ArticleIcon />} disabled={setupDisabled} onClick={openAddArticleDialog}>
                  {translate('kb_setup_page.add_article', { defaultValue: 'Add Article' })}
                </Button>
              </Stack>
              <TextField fullWidth disabled={setupDisabled} label={translate('kb_setup_page.search_articles', { defaultValue: 'Search Articles' })} value={search} onChange={(e) => setSearch(e.target.value)} />
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" disabled={setupDisabled} onClick={() => fetchKBData(search)}>{translate('common.search', { defaultValue: 'Search' })}</Button>
                <Button variant="text" disabled={setupDisabled} onClick={() => { setSearch(''); fetchKBData(''); }}>{translate('common.clear', { defaultValue: 'Clear' })}</Button>
              </Stack>
              {articles.map((article) => (
                <Paper key={article.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2">{article.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {htmlToPreviewText(article.content).slice(0, 180)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {helpBaseUrl}/{toPathSlug(article.slug || article.title)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={article.status === 'published' ? translate('kb_setup_page.published', { defaultValue: 'Published' }) : translate('kb_setup_page.draft', { defaultValue: 'Draft' })} color={article.status === 'published' ? 'primary' : 'default'} />
                      {article.status !== 'published' && <Button size="small" variant="outlined" disabled={setupDisabled} onClick={() => publishArticle(article)}>{translate('kb_setup_page.publish', { defaultValue: 'Publish' })}</Button>}
                      <Button size="small" variant="outlined" disabled={setupDisabled} onClick={() => editArticle(article)}>{translate('common.edit', { defaultValue: 'Edit' })}</Button>
                      <Button size="small" color="error" variant="outlined" disabled={setupDisabled} onClick={() => setDeleteTarget(article)}>{translate('common.delete', { defaultValue: 'Delete' })}</Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Stack>
      )}

      <Dialog
        open={articleDialogOpen}
        onClose={closeArticleDialog}
        fullWidth
        maxWidth="lg"
        TransitionProps={{ onEntered: hydrateArticleEditor }}
        PaperProps={{
          sx: {
            width: 'min(1200px, calc(100vw - 32px))',
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 48px)',
            overflowX: 'hidden',
          },
        }}
      >
        <DialogTitle>{articleForm.id ? translate('kb_setup_page.edit_article', { defaultValue: 'Edit Article' }) : translate('kb_setup_page.add_article', { defaultValue: 'Add Article' })}</DialogTitle>
        <DialogContent dividers sx={{ overflowX: 'hidden' }}>{renderArticleEditor()}</DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="text" onClick={closeArticleDialog} disabled={saveLoader}>{translate('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <LoadingButton variant="contained" loading={saveLoader} disabled={setupDisabled} onClick={saveArticle}>{translate('kb_setup_page.save_article', { defaultValue: 'Save Article' })}</LoadingButton>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleteLoader && setDeleteTarget(null)}
        onConfirm={deleteArticle}
        title={translate('common.confirm_deletion', { defaultValue: 'Confirm deletion' })}
        message={translate('kb_setup_page.delete_article_message', {
          title: deleteTarget?.title,
          defaultValue: 'Delete article "{{title}}"?',
        })}
        submitButtonName={translate('common.delete', { defaultValue: 'Delete' })}
        loading={deleteLoader}
      />
    </KBSetupShell>
  );
}
